/**
 * Subscriptions View Component
 * Displays all available subscription plans with modern UI
 * Used in both Settings screen and Checkout flow
 */

import React, {useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {
  View,
  ScrollView,
  Text,
  TouchableOpacity,
  ViewStyle,
  TextStyle,
  SafeAreaView,
  ActivityIndicator,
  Image,
} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius} from '~constants/design';
import {
  getAllSubscriptionPlans,
  SUBSCRIPTION_PLAN_ORDER,
  getPlanDetails,
} from '~constants/subscription';
import {
  SubscriptionPlan,
  SubscriptionPlanDetails,
  SubscriptionType,
} from '~types/subscription/subscription.types';
import SubscriptionCard from './SubscriptionCard';
import ModernHeader from '~components/ModernHeader/ModernHeader';
import SubscriptionCheckoutModal from './SubscriptionCheckoutModal';
import {useGetSubscriptionPlans} from '~hooks/subscription/useGetSubscriptionPlans';
import {useUpgradeDowngradeSubscription} from '~hooks/subscription/useUpgradeDowngradeSubscription';
import {Alert} from 'react-native';

interface SubscriptionsViewProps {
  /**
   * If true, shows the view in a checkout context (with context-specific messaging)
   */
  isCheckoutFlow?: boolean;
  /**
   * Callback when user selects a subscription
   */
  onSubscriptionSelect?: (planId: SubscriptionPlan, type: SubscriptionType) => void;
  /**
   * Current active subscription plan (if any)
   */
  currentPlanId?: SubscriptionPlan;
  /**
   * Whether to show the header
   */
  showHeader?: boolean;
  /**
   * Custom header title
   */
  headerTitle?: string;
}

const SubscriptionsView: React.FC<SubscriptionsViewProps> = ({
  isCheckoutFlow = false,
  onSubscriptionSelect,
  currentPlanId,
  showHeader = true,
  headerTitle = 'Pyxi Plans',
}) => {
  const {colors, themeMode} = useTheme();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanDetails | null>(null);
  const [selectedType, setSelectedType] = useState<SubscriptionType | null>(null);
  const [isCheckoutModalVisible, setIsCheckoutModalVisible] = useState(false);

  const {mutate: upgradeDowngrade, isLoading: isUpgradingDowngrading} = useUpgradeDowngradeSubscription();

  // Fetch subscription plans using React Query hook
  const {data: subscriptionPlans = [], isLoading, error} = useGetSubscriptionPlans();

  // Fallback to static data if API fails
  const displayedPlans = useMemo(
    () => (subscriptionPlans.length > 0 ? subscriptionPlans : getAllSubscriptionPlans()),
    [subscriptionPlans],
  );

  const handleUpgradeDowngrade = useCallback((plan: SubscriptionPlanDetails) => {
    const currentPlan = displayedPlans.find(p => p.id === currentPlanId) || 
                       (currentPlanId ? getPlanDetails(currentPlanId as SubscriptionPlan) : null);
    
    const isUpgrade = currentPlan && 
      (plan.pricing?.monthly || 0) > (currentPlan.pricing?.monthly || 0);

    const isDowngrade = currentPlan && 
      (plan.pricing?.monthly || 0) < (currentPlan.pricing?.monthly || 0);

    const actionText = isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Change';
    
    let alertTitle = `${actionText} Subscription`;
    let alertMessage = `Are you sure you want to ${actionText.toLowerCase()} your plan to ${plan.name}?`;

    if (isUpgrade) {
      alertMessage += '\n\nNote: The price difference for the remaining days of your current plan will be charged immediately.';
    } else if (isDowngrade) {
      alertMessage += '\n\nNote: Your plan will be downgraded immediately.';
    }

    Alert.alert(
      alertTitle,
      alertMessage,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: actionText,
          onPress: () => {
            upgradeDowngrade({new_plan_id: plan.id}, {
              onSuccess: () => {
                Alert.alert('Success', `Successfully ${actionText.toLowerCase()}d your subscription to ${plan.name}.`);
              },
              onError: (error: any) => {
                Alert.alert('Error', error.message || 'Failed to change subscription plan.');
              }
            });
          }
        }
      ]
    );
  }, [currentPlanId, upgradeDowngrade, displayedPlans]);

  const styles = useMemo(() => {
    const containerStyle: ViewStyle = {
      flex: 1,
      backgroundColor: colors.background,
    };

    const scrollContentStyle: ViewStyle = {
      paddingTop: spacing.sm,
      paddingBottom: spacing.lg,
    };

    const headerSectionStyle: ViewStyle = {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.sm,
      marginBottom: spacing.md,
    };

    const titleStyle: TextStyle = {
      fontSize: 20,
      fontWeight: '800',
      color: colors.text,
      marginBottom: spacing.xs,
    };

    const subtitleStyle: TextStyle = {
      fontSize: 12,
      color: colors.textSecondary,
      lineHeight: 18,
    };

    const plansContainerStyle: ViewStyle = {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      gap: spacing.md,
    };

    const ctaContainerStyle: ViewStyle = {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      marginTop: spacing.sm,
    };

    const ctaButtonStyle: ViewStyle = {
      backgroundColor: colors.primary,
      paddingVertical: spacing.lg,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
    };

    const ctaButtonTextStyle: TextStyle = {
      fontSize: 16,
      fontWeight: '700',
      color: colors.textInverse,
      letterSpacing: 0.5,
    };

    const featureHighlightStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: spacing.xs,
    };

    const featureIconStyle: TextStyle = {
      fontSize: 16,
      marginRight: spacing.sm,
    };

    const featureTextStyle: TextStyle = {
      fontSize: 13,
      color: colors.text,
      flex: 1,
    };

    return {
      containerStyle,
      scrollContentStyle,
      headerSectionStyle,
      titleStyle,
      subtitleStyle,
      plansContainerStyle,
      ctaContainerStyle,
      ctaButtonStyle,
      ctaButtonTextStyle,
      featureHighlightStyle,
      featureIconStyle,
      featureTextStyle,
    };
  }, [colors, themeMode]);

  const handleSelectMonthly = useCallback(
    (plan: SubscriptionPlanDetails) => {
      setSelectedPlan(plan);
      setSelectedType(SubscriptionType.MONTHLY);
      setIsCheckoutModalVisible(true);
    },
    [],
  );

  const handleCheckoutComplete = useCallback(
    (planId: SubscriptionPlan, type: SubscriptionType) => {
      setIsCheckoutModalVisible(false);
      if (onSubscriptionSelect) {
        onSubscriptionSelect(planId, type);
      }
    },
    [onSubscriptionSelect],
  );

  const handleCloseCheckout = useCallback(() => {
    setIsCheckoutModalVisible(false);
    setSelectedPlan(null);
    setSelectedType(null);
  }, []);

  return (
    <SafeAreaView style={styles.containerStyle}>
      {showHeader && (
        <ModernHeader
          title={headerTitle}
          showBackButton={true}
        />
      )}

      {isLoading ? (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: colors.background,
          }}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={{color: colors.text, marginTop: spacing.md}}>
            Loading subscription plans...
          </Text>
        </View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContentStyle}>
          {/* Pyxi Logo */}
          <View
            style={{
              alignItems: 'center',
              paddingVertical: spacing.md,
              paddingHorizontal: spacing.lg,
            }}>
            <Image
              source={require('~assets/images/ic_launcher.png')}
              style={{
                width: 120,
                height: 120,
                resizeMode: 'contain',
              }}
            />
          </View>

          {/* Header Section with Context */}
          <View
            style={styles.headerSectionStyle}>
            <Text style={styles.titleStyle}>
              {isCheckoutFlow ? '🎉 Upgrade Your Experience' : 'Pyxi Plans'}
            </Text>
            <Text style={styles.subtitleStyle}>
              {isCheckoutFlow
                ? 'Unlock exclusive features and unlimited access during checkout.'
                : 'Choose the perfect plan for your Pyxi journey.'}
            </Text>
          </View>

          {/* Subscription Plans */}
          <View style={styles.plansContainerStyle}>
            {displayedPlans.map((plan, index) => (
              <SubscriptionCard
                key={plan.id}
                plan={plan}
                index={index}
                onSelectMonthly={handleSelectMonthly}
                isCurrentPlan={currentPlanId === plan.id}
                currentPlanId={currentPlanId}
                onUpgradeDowngrade={handleUpgradeDowngrade}
                isProcessing={isUpgradingDowngrading}
                allPlans={displayedPlans}
              />
            ))}
          </View>

          {/* Additional Info */}
          <View
            style={styles.ctaContainerStyle}>
            <View
              style={{
                backgroundColor: colors.primary + '12',
                borderWidth: 1,
                borderColor: colors.primary + '30',
                padding: spacing.lg,
                borderRadius: borderRadius.lg,
              }}>
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: '700',
                  color: colors.primary,
                  marginBottom: spacing.sm,
                  letterSpacing: 0.3,
                }}>
                ✓ All Plans Include
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  lineHeight: 18,
                }}>
                • Access to unlimited dinners{'\n'}
                • Priority matching on all dates{'\n'}
                • Reduced no-show fees{'\n'}
                • 24/7 customer support{'\n'}
                • Easy cancellation anytime
              </Text>
            </View>
          </View>
          {/* Empty space for better scrolling */}
          <View style={{height: spacing.xl}} />
        </ScrollView>
      )}

      {/* Subscription Checkout Modal */}
      {selectedPlan && selectedType && (
        <SubscriptionCheckoutModal
          isVisible={isCheckoutModalVisible}
          plan={selectedPlan}
          subscriptionType={selectedType}
          onClose={handleCloseCheckout}
          onSuccess={handleCheckoutComplete}
        />
      )}
    </SafeAreaView>
  );
};

export default SubscriptionsView;
